import * as Yup from "yup";

import { emailRecipientsValidation } from "components/Business/BusinessForm/EmailRecipients/validation";
import { addressValidationSchema } from "components/Form/Fields/AddressForm";
import { BankingDetailsValidationSchema } from "components/Form/Fields/BankingDetailsForm";
import { tradingTermValidation } from "components/Form/FormikControls/TradingTerms/validation";
import { vendorCommissionBandsValidation } from "components/Form/FormikControls/VendorCommissionBands";

import { validateSplitAllocations } from "lib/splits";
import {
  ABNValidator,
  OptionalACNValidator,
  nameValidation,
} from "lib/validation";

import { SplitType } from "constants/splits";

import { DefaultSplit } from "./types";

export interface ValidationSchemaOptions {
  includeVendorCommissionBands?: boolean;
  includeDefaultBuyerSplit?: boolean;
  includeDefaultVendorSplit?: boolean;
}

export const defaultSplitValidation = Yup.object().test(
  function (validationValue): boolean | Yup.ValidationError {
    const values = validationValue as unknown as DefaultSplit | undefined;

    if (!values || !values.splits?.length) {
      return true;
    }

    const errors = validateSplitAllocations(
      values.splits,
      SplitType.PERCENTAGE,
      null,
    );

    if (errors.length > 0) {
      return this.createError({
        path: `${this.path}.splits`,
        message: errors.join(" "),
      });
    }
    return true;
  },
);

export { nameValidation };

export const publicDisplayNameValidation = Yup.string()
  .label("Public Display Name")
  .max(200)
  .nullable();

export const shortCodeValidation = Yup.string()
  .label("Agent Account Reference")
  .max(16)
  .nullable();

export const shortCodeSaleyardValidation = Yup.string()
  .label("Saleyard Account Reference")
  .max(16)
  .nullable();

export const slugValidation = Yup.string()
  .label("Agent Account Reference")
  .matches(
    /^[[A-Za-z0-9\-_]+$/,
    "Agent Account Reference can only contain letters, numbers, underscores and hyphens",
  )
  .max(20)
  .nullable();

export const shortCodeAuctionsPlusValidation = Yup.string()
  .label("Auctions Plus Shortcode")
  .max(50)
  .nullable();

export const bpayCustomerReferenceNumberValidation = Yup.string()
  .matches(
    /^[0-9]{3,20}$/,
    "CRN must be all digits and between 3 and 20 in length",
  )
  .nullable();

export const myobCustomerCardIdValidation = Yup.string()
  .label("Customer Card Id")
  .max(15)
  .nullable();

export const myobSupplierCardIdValidation = Yup.string()
  .label("Supplier Card Id")
  .max(15)
  .nullable();

export const identicalCardIdsValidation = Yup.mixed().test(
  "Customer Card Ids are not identical",
  "The Customer Card ID and Supplier Card ID should not be identical.",
  (value, context): boolean => {
    if (!context) {
      return true;
    }
    // make sure customer card id and supplier card id are not identical
    const { myobCustomerCardId, myobSupplierCardId } = context.parent;
    if (
      myobCustomerCardId != null &&
      myobSupplierCardId != null &&
      myobCustomerCardId === myobSupplierCardId
    ) {
      return false;
    }
    return true;
  },
);

export const getBusinessValidationSchema = (
  options: ValidationSchemaOptions = {
    includeVendorCommissionBands: false,
    includeDefaultBuyerSplit: false,
    includeDefaultVendorSplit: false,
  },
) => {
  const shape: Record<string, Yup.AnySchema> = {
    name: nameValidation,
    publicDisplayName: publicDisplayNameValidation,
    shortCode: shortCodeValidation,
    shortCodeSaleyard: shortCodeSaleyardValidation,
    slug: slugValidation,
    shortCodeAuctionsPlus: shortCodeAuctionsPlusValidation,
    abn: ABNValidator,
    acn: OptionalACNValidator,
    bankingDetails: BankingDetailsValidationSchema.nullable(),
    address: addressValidationSchema.nullable(),
    bpayCustomerReferenceNumber: bpayCustomerReferenceNumberValidation,
    generateBpayCRN: Yup.boolean(),
    myobCustomerCardId: myobCustomerCardIdValidation,
    myobSupplierCardId: myobSupplierCardIdValidation,
    identicalCardIds: identicalCardIdsValidation,
    tradingTerms: tradingTermValidation,
    emailRecipients: emailRecipientsValidation,
  };
  if (options.includeVendorCommissionBands) {
    shape.vendorCommissionBands = vendorCommissionBandsValidation;
  }

  if (options.includeDefaultBuyerSplit) {
    shape.defaultBuyerSplit = defaultSplitValidation;
  }
  if (options.includeDefaultVendorSplit) {
    shape.defaultVendorSplit = defaultSplitValidation;
  }

  return Yup.object().shape(shape);
};
